import type { Position, StepType } from "@reactour/tour";
import rawContent from "./tour-content.json";

export type OnboardingModuleStatus = "completed" | "skipped" | "pending";
export type OnboardingStepKind =
    | "welcome"
    | "navigation"
    | "standard"
    | "completion";
export type OnboardingCondition = "single_org" | "multi_org";

export const CENTER_SELECTOR = "[data-onboarding-center]";

const STRUCTURAL_MODULE_IDS = new Set(["welcome"]);

const isModuleAccessible = (
    mod: RawModule,
    userPermissions: string[],
    userRole: UserRole,
): boolean =>
    (!mod.allowedRoles?.length || mod.allowedRoles.includes(userRole)) &&
    (!mod.requiredPermissions?.length ||
        mod.requiredPermissions.every((p) => userPermissions.includes(p)));

export type UserRole = "staff" | "partner" | "customer";

interface StepSettings {
    position?: string;
    actionKey?: string;
    actionAfterKey?: string;
    flashKey?: string;
    interactive?: boolean;
}

type RoleString =
    | string
    | (Partial<Record<UserRole, string>> & { default?: string });

const resolveRoleString = (
    field: RoleString | undefined,
    userRole: UserRole,
): string | undefined => {
    if (field === undefined) return undefined;
    if (typeof field === "string") return field;
    return field[userRole] ?? field.default;
};

interface RawStep extends StepSettings {
    kind?: string;
    title: RoleString;
    body: RoleString;
    hint?: RoleString;
    interaction?: RoleString;
    selector?: string;
    overflowPosition?: "top-center";
    highlightedSelectors?: string[];
    allowedRoles?: string[];
    requiredConditions?: OnboardingCondition[];
    advanceOnNavigation?: boolean;
    advanceAtRoute?: string;
    advanceOnClick?: boolean;
    noBack?: boolean;
}

interface RawModule {
    label: string;
    enabled: boolean;
    estimatedMinutes?: number;
    dependsOn?: string;
    requiredPermissions?: string[];
    allowedRoles?: string[];
    steps: RawStep[];
}

interface TourContent {
    defaults: StepSettings;
    completion: { title: string; body: string };
    moduleOrder: string[];
    modules: Record<string, RawModule>;
}

const content = rawContent as TourContent;

export const getWelcomeContent = (
    userRole: UserRole,
): {
    title: string;
    body: string;
} => {
    const step = content.modules["welcome"]?.steps[0];
    return {
        title:
            resolveRoleString(step?.title, userRole) ?? "Welcome to Illuminate",
        body: resolveRoleString(step?.body, userRole) ?? "",
    };
};

export type OnboardingStepType = StepType & { resolved: ResolvedStep };

export interface ResolvedStep {
    moduleId: string;
    moduleLabel: string;
    dependsOn?: string;
    kind: OnboardingStepKind;
    title: string;
    body: string;
    hint?: string;
    interaction?: string;
    selector: string;
    overflowPosition?: "top-center";
    highlightedSelectors?: string[];
    actionKey?: string;
    actionAfterKey?: string;
    flashKey?: string;
    interactive: boolean;
    advanceOnNavigation: boolean;
    advanceAtRoute?: string;
    advanceOnClick: boolean;
    noBack: boolean;
    position?: Position;
}

export interface SelectableModule {
    id: string;
    label: string;
    estimatedMinutes?: number;
}

const resolveKind = (kind?: string): OnboardingStepKind => {
    switch (kind?.toLowerCase()) {
        case "welcome":
            return "welcome";
        case "navigation":
            return "navigation";
        case "completion":
            return "completion";
        default:
            return "standard";
    }
};

// Exported for unit tests.
export const buildResolvedStep = ({
    moduleId,
    moduleLabel,
    dependsOn,
    step,
    defaults,
    userRole,
}: {
    moduleId: string;
    moduleLabel: string;
    dependsOn?: string;
    step: RawStep;
    defaults: StepSettings;
    userRole: UserRole;
}): ResolvedStep => {
    const hasAnchor = !!step.selector;
    const position = step.position ?? defaults.position;

    return {
        moduleId,
        moduleLabel,
        dependsOn,
        kind: resolveKind(step.kind),
        title: resolveRoleString(step.title, userRole) ?? "",
        body: resolveRoleString(step.body, userRole) ?? "",
        hint: resolveRoleString(step.hint, userRole),
        interaction: resolveRoleString(step.interaction, userRole),
        // No selector → fall back to the invisible centre anchor so Reactour has a valid DOM target.
        selector: step.selector ?? CENTER_SELECTOR,
        overflowPosition: step.overflowPosition,
        highlightedSelectors: step.highlightedSelectors,
        actionKey: step.actionKey ?? defaults.actionKey,
        actionAfterKey: step.actionAfterKey ?? defaults.actionAfterKey,
        flashKey: step.flashKey ?? defaults.flashKey,
        interactive: step.interactive ?? defaults.interactive ?? false,
        advanceOnNavigation: step.advanceOnNavigation ?? false,
        advanceAtRoute: step.advanceAtRoute ?? undefined,
        advanceOnClick: step.advanceOnClick ?? false,
        noBack: step.noBack ?? false,
        position: (position ?? (hasAnchor ? undefined : "center")) as
            | Position
            | undefined,
    };
};

export const getSelectableModules = (
    userPermissions: string[],
    userRole: UserRole,
): SelectableModule[] => {
    return content.moduleOrder
        .filter((moduleId) => !STRUCTURAL_MODULE_IDS.has(moduleId))
        .filter((moduleId) => {
            const module = content.modules[moduleId];
            if (!module || !module.enabled || module.steps.length === 0) {
                return false;
            }
            // Dependent modules are auto-appended after selection, not shown as checkboxes.
            if (module.dependsOn) return false;
            return isModuleAccessible(module, userPermissions, userRole);
        })
        .map((moduleId) => {
            const module = content.modules[moduleId];

            // Include time from qualifying dependent modules in the displayed estimate.
            let dependentMinutes = 0;
            for (const depId of content.moduleOrder) {
                const dep = content.modules[depId];
                if (!dep?.enabled || !dep.steps.length) continue;
                if (dep.dependsOn !== moduleId) continue;
                if (!isModuleAccessible(dep, userPermissions, userRole))
                    continue;
                dependentMinutes += dep.estimatedMinutes ?? 0;
            }

            const total = (module.estimatedMinutes ?? 0) + dependentMinutes;
            return {
                id: moduleId,
                label: module.label,
                estimatedMinutes: total > 0 ? total : undefined,
            };
        });
};

// Appends dependent modules after the user confirms their selection.
export const expandWithDependents = (
    selectedIds: string[],
    userPermissions: string[],
    userRole: UserRole,
): string[] => {
    const selectedSet = new Set(selectedIds);
    const result = [...selectedIds];

    for (const moduleId of content.moduleOrder) {
        const module = content.modules[moduleId];
        if (
            !module ||
            !module.enabled ||
            module.steps.length === 0 ||
            !module.dependsOn
        ) {
            continue;
        }
        if (!selectedSet.has(module.dependsOn)) continue;
        if (!isModuleAccessible(module, userPermissions, userRole)) continue;
        const parentIndex = result.indexOf(module.dependsOn);
        result.splice(parentIndex + 1, 0, moduleId);
    }

    return result;
};

export const buildTour = (
    selectedModuleIds: string[],
    userRole: UserRole,
    conditions: OnboardingCondition[] = [],
): ResolvedStep[] => {
    const steps: ResolvedStep[] = [];
    const defaults = content.defaults ?? {};

    for (const moduleId of selectedModuleIds) {
        const module = content.modules[moduleId];
        if (!module || !module.enabled || module.steps.length === 0) {
            continue;
        }
        for (const step of module.steps) {
            if (
                step.allowedRoles?.length &&
                !step.allowedRoles.includes(userRole)
            ) {
                continue;
            }
            if (
                step.requiredConditions?.length &&
                !step.requiredConditions.some((c) => conditions.includes(c))
            ) {
                continue;
            }
            steps.push(
                buildResolvedStep({
                    moduleId,
                    moduleLabel: module.label,
                    dependsOn: module.dependsOn,
                    step,
                    defaults,
                    userRole,
                }),
            );
        }
    }

    if (steps.length > 0) {
        steps.push(
            buildResolvedStep({
                moduleId: "completion",
                moduleLabel: content.completion.title,
                step: {
                    kind: "completion",
                    title: content.completion.title,
                    body: content.completion.body,
                },
                defaults,
                userRole,
            }),
        );
    }

    return steps;
};

export const getDependentModuleIds = (
    moduleId: string,
    steps: ResolvedStep[],
): Set<string> => {
    const dependents = new Set<string>();
    for (const step of steps) {
        if (step.dependsOn === moduleId) {
            dependents.add(step.moduleId);
        }
    }
    return dependents;
};

export const toResolvedSteps = (steps: StepType[]): ResolvedStep[] =>
    (steps as OnboardingStepType[]).map((step) => step.resolved);

export const canGoToNextStep = (
    steps: ResolvedStep[],
    index: number,
): boolean => {
    const step = steps[index];
    if (!step || step.kind === "completion") return false;
    return !step.advanceOnNavigation && !step.advanceOnClick;
};

export const canGoToPreviousStep = (
    steps: ResolvedStep[],
    index: number,
): boolean => {
    const step = steps[index];
    if (!step || step.kind === "completion" || step.noBack) return false;
    const standardSteps = steps.filter(
        (standardStep) => standardStep.kind === "standard",
    );
    return standardSteps.indexOf(step) > 0;
};
